import {
    type Block,
    type Document,
    type Fragment,
    type Inline,
    TextRange,
    type Value,
} from '@gitbook/slate';
import { List, memoizeClassMethods, Record } from '@gitbook/util-immutable';

import type { Plugin } from './plugin';

/*
 * Stack of plugins passed when rendering the editor.
 */
export default class PluginsStack extends Record<{ plugins: Plugin[] }>({
    plugins: [],
}) {
    public static create(plugins: Plugin[]): PluginsStack {
        return PluginsStack.createRecord({ plugins });
    }

    public readonly plugins: Plugin[];

    /**
     * Get the decorations for the node from a `stack`.
     */
    public getDecorations(node: Document | Fragment | Block | Inline): List<TextRange> {
        const decorations = this.find('decorateNode', node);
        const list = List.create<TextRange>((decorations || []).map((el) => TextRange.create(el)));
        return list;
    }

    /**
     * Get the decorations for the value from a `stack`.
     */
    public getValueDecorations(value: Value): List<TextRange> {
        const { plugins } = this;
        const decorations = value.decorations.toArray();

        for (const plugin of plugins) {
            const fn = plugin.decorateValue;
            if (!fn) {
                continue;
            }

            const ret = fn(value);
            if (ret) {
                decorations.push(...ret.map((el) => TextRange.create(el)));
            }
        }

        return List.create(decorations);
    }

    /**
     * Iterate the plugins with `property`, returning the first non-null value.
     */
    public find<T extends keyof Plugin>(
        property: T,
        // @ts-expect-error
        ...args: Parameters<Plugin[T]>
        // @ts-expect-error
    ): ReturnType<Plugin[T]> | undefined {
        const { plugins } = this;
        for (const plugin of plugins) {
            const fn = plugin[property];
            if (!fn) {
                continue;
            }

            // @ts-expect-error
            const ret = fn(...args);
            if (ret != null) {
                // @ts-expect-error
                return ret;
            }
        }
    }

    /**
     * Iterate the plugins with `property`, breaking on any a non-null values.
     */
    // @ts-expect-error
    public run<T extends keyof Plugin>(property: T, ...args: Parameters<Plugin[T]>): void {
        const { plugins } = this;
        for (const plugin of plugins) {
            const fn = plugin[property];
            if (!fn) {
                continue;
            }

            // @ts-expect-error
            const ret = fn(...args);
            if (ret) {
                return;
            }
        }
    }
}

/*
 * Memoize read methods.
 */
memoizeClassMethods(PluginsStack, ['getValueDecorations', 'getDecorations']);
